import React, { useState, useEffect, useRef } from 'react'
import { X, Plus, Trash2, RotateCcw, Search } from 'lucide-react'
import type { Order, OrderCreateData, OrderUpdateData, AdditionalStop, PricingDetail } from '@/api/orders'
import { createOrder } from '@/api/orders'
import { lookupFlight } from '@/api/flights'
import type { FlightInfo } from '@/api/flights'
import { Input } from "@/components/ui/input"
import { InputWithClear } from "@/components/ui/input-with-clear"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { MonthDayPicker } from "@/components/ui/month-day-picker"
import { TimePicker } from "@/components/ui/time-picker"
import { Counter } from "@/components/ui/counter"
import { cn } from "@/lib/utils"
import type { Driver, Tenant, Customer } from '@/types'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'
import CustomerCombobox from '@/components/ui/CustomerCombobox'
import { leaveApi } from '@/api/leaves'

interface OrderDrawerProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: OrderCreateData | OrderUpdateData) => Promise<void>
  order?: Order | null
  drivers: Driver[]
  customers?: Customer[]
  onRequestAddCustomer?: () => void
  autoSelectCustomerId?: string
  previewImageUrl?: string
  aiRawText?: string
  onCustomSignFileChange?: (file: File | null) => void
}

interface ReturnTripData {
  order_category: string
  pickup_date: string
  pickup_time: string
  pickup_location: string
  dropoff_location: string
  flight_number?: string
  name_board: boolean
  pricing: PricingDetail
}

interface OrderFormData {
  order_category: string
  status: string
  customer_id?: string
  passenger_name: string
  passenger_phone: string
  passenger_count: number
  pickup_date: string
  pickup_time: string
  pickup_location: string
  dropoff_location: string
  is_round_trip: boolean
  flight_number?: string
  departure_time?: string
  arrival_time?: string
  arrival_terminal?: string
  vehicle_type: string
  luggage_count: number
  child_seat: number
  booster_seat: number
  name_board: boolean
  is_signed: boolean
  pricing: PricingDetail
  payment_method: string
  driver_notes?: string
  driver_id: string
  driver_name: string
  driver_phone: string
  vehicle_number: string
  additional_stops?: AdditionalStop[]
  return_trip?: ReturnTripData
}

const AIRPORT_BASES = ["桃園機場", "松山機場", "台中清泉崗機場", "高雄小港機場"]
const TAOYUAN_TERMINALS = ["第1航廈", "第2航廈"]

const DEFAULT_VEHICLE_OPTIONS = ["小車", "休旅車", "大車"]
const DEFAULT_PAYMENT_OPTIONS = ["現金", "刷卡", "轉帳", "月結", "匯款"]
const STATUS_OPTIONS = ["待派車", "已派車", "進行中", "已完成", "已取消"]

const getReversedCategory = (cat: string) => {
  if (cat === '送機') return '接機'
  if (cat === '接機') return '送機'
  return '其他'
}

const getEmptyPricing = (isTaxInclusiveDefault = true): PricingDetail => ({
  total_amount: 0, driver_amount: 0, base_fare: 0,
  night_surcharge: 0, cross_district_fee: 0,
  driver_fee: 0, vendor_rebate: 0, company_rebate: 0,
  is_tax_inclusive: isTaxInclusiveDefault,
})

const getTodayDate = () => {
  const today = new Date()
  return `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`
}

const formatToISO8601 = (dateStr: string, timeStr: string) => {
  const currentYear = new Date().getFullYear()
  const [month, day] = dateStr.split('-')
  const [hours, minutes] = timeStr.split(':')
  return `${currentYear}-${month}-${day}T${hours}:${minutes}:00`
}

const parseISOToMonthDay = (isoString: string) => {
  const d = new Date(isoString)
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`
}

const parseISOToTime = (isoString: string) => {
  const d = new Date(isoString)
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

const normalizeAirportLocation = (loc: string): string => {
  if (loc === '桃園機場一航廈') return '桃園機場第1航廈'
  if (loc === '桃園機場二航廈') return '桃園機場第2航廈'
  if (loc === '桃園機場三航廈') return '桃園機場第3航廈'
  return loc
}

const getInitialFormData = (): OrderFormData => {
  // 從 localStorage 讀取租戶預設設定
  const tenantStr = localStorage.getItem('tenant')
  const isTaxInclusiveDefault = tenantStr ? (JSON.parse(tenantStr).form_config?.pricing_is_tax_inclusive_default ?? true) : true

  return {
    order_category: '送機', status: '待派車', customer_id: undefined,
    passenger_name: '貴賓', passenger_phone: '', passenger_count: 1,
    pickup_date: getTodayDate(), pickup_time: '00:00',
    pickup_location: '', dropoff_location: '',
    is_round_trip: false, flight_number: '', departure_time: '', arrival_time: '', arrival_terminal: '', vehicle_type: '',
    luggage_count: 0, child_seat: 0, booster_seat: 0,
    name_board: false, is_signed: false,
    pricing: getEmptyPricing(isTaxInclusiveDefault),
    payment_method: '現金',
    driver_id: '', driver_name: '', driver_phone: '', vehicle_number: '',
  }
}

const buildFormFromOrder = (order: any): OrderFormData => {
  let pickupDate = getTodayDate(), pickupTime = '00:00'
  if (order.pickup_datetime) {
    pickupDate = parseISOToMonthDay(order.pickup_datetime)
    pickupTime = parseISOToTime(order.pickup_datetime)
  }
  const category = order.order_category || '送機'

  // 優先使用新的 airport/terminal 字段，否則使用 location 字符串
  let pickupLoc = order.pickup_location || ''
  if (order.pickup_airport && order.pickup_terminal) {
    pickupLoc = `${order.pickup_airport}${order.pickup_terminal}`
  } else {
    pickupLoc = normalizeAirportLocation(pickupLoc)
  }

  let dropoffLoc = order.dropoff_location || ''
  if (order.dropoff_airport && order.dropoff_terminal) {
    dropoffLoc = `${order.dropoff_airport}${order.dropoff_terminal}`
  } else {
    dropoffLoc = normalizeAirportLocation(dropoffLoc)
  }

  return {
    order_category: category,
    status: order.status || '待派車',
    customer_id: order.customer_id || undefined,
    passenger_name: order.passenger_name || '貴賓',
    passenger_phone: order.passenger_phone || '',
    passenger_count: order.passenger_count || 1,
    pickup_date: pickupDate, pickup_time: pickupTime,
    pickup_location: category === '接機' ? (pickupLoc || '桃園機場第1航廈') : pickupLoc,
    dropoff_location: category === '送機' ? (dropoffLoc || '桃園機場第1航廈') : dropoffLoc,
    is_round_trip: order.is_round_trip || false,
    flight_number: order.flight_number || '',
    departure_time: order.departure_time || '',
    arrival_time: order.arrival_time || '',
    arrival_terminal: order.arrival_terminal || '',
    vehicle_type: order.vehicle_type || '',
    luggage_count: order.luggage_count || 0,
    child_seat: order.child_seat || 0,
    booster_seat: order.booster_seat || 0,
    name_board: order.name_board || false,
    is_signed: order.is_signed || false,
    pricing: {
      total_amount: order.pricing?.total_amount || 0,
      driver_amount: order.pricing?.driver_amount || 0,
      base_fare: order.pricing?.base_fare || order.pricing?.total_amount || 0,
      night_surcharge: order.pricing?.night_surcharge || 0,
      cross_district_fee: order.pricing?.cross_district_fee || 0,
      driver_fee: order.pricing?.driver_fee || 0,
      vendor_rebate: order.pricing?.vendor_rebate || 0,
      company_rebate: order.pricing?.company_rebate || 0,
      is_tax_inclusive: order.pricing?.is_tax_inclusive || false,
    },
    payment_method: order.payment_method || '現金',
    driver_notes: order.driver_notes || '',
    driver_id: order.driver_id || '',
    driver_name: order.driver_name || '',
    driver_phone: order.driver_phone || '',
    vehicle_number: order.vehicle_number || '',
    additional_stops: order.additional_stops || [],
  }
}

const calcTotal = (p: PricingDetail, nameboard: boolean) =>
  (p.base_fare || 0) + (p.night_surcharge || 0) + (p.cross_district_fee || 0) + (nameboard ? 100 : 0)

function ImagePreviewPanel({ url }: { url: string }) {
  const [collapsed, setCollapsed] = React.useState(false)
  return (
    <div className="lg:w-[450px] lg:shrink-0 lg:border-r border-b lg:border-b-0 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col lg:overflow-hidden">
      {/* 標題列：手機可收折 */}
      <button
        type="button"
        onClick={() => setCollapsed(v => !v)}
        className="flex items-center justify-between px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 shrink-0 lg:cursor-default"
      >
        <span>原始圖片</span>
        <span className="lg:hidden text-gray-400">{collapsed ? '▼ 展開' : '▲ 收折'}</span>
      </button>
      {/* 圖片 */}
      {!collapsed && (
        <div className="overflow-auto p-2 flex items-start justify-center lg:flex-1">
          <img
            src={url}
            alt="辨識來源圖片"
            className="max-w-full rounded-md object-contain shadow-sm"
            style={{ maxHeight: '40vh' }}
          />
        </div>
      )}
    </div>
  )
}

export default function OrderDrawer({ isOpen, onClose, onSubmit, order, drivers, customers = [], onRequestAddCustomer, autoSelectCustomerId, previewImageUrl, aiRawText, onCustomSignFileChange }: OrderDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [returnTripError, setReturnTripError] = useState<string | null>(null)
  const [newStopInput, setNewStopInput] = useState('')
  const timePickerRef = useRef<{ focus: () => void }>(null)
  const [flightInfo, setFlightInfo] = useState<FlightInfo | null>(null)
  const [flightMsg, setFlightMsg] = useState<string>('')
  const [flightLoading, setFlightLoading] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState<OrderFormData>(() => order ? buildFormFromOrder(order) : getInitialFormData())
  const [vehicleOptions, setVehicleOptions] = useState<string[]>(DEFAULT_VEHICLE_OPTIONS)
  const [paymentOptions, setPaymentOptions] = useState<string[]>(DEFAULT_PAYMENT_OPTIONS)
  const [hiddenFields, setHiddenFields] = useState<string[]>([])
  const [onLeaveDriverIds, setOnLeaveDriverIds] = useState<string[]>([])
  const [customSignEnabled, setCustomSignEnabled] = useState(false)
  const [customSignFile, setCustomSignFile] = useState<File | null>(null)
  const [customSignPreview, setCustomSignPreview] = useState<string | null>(null)

  // helper: 是否隱藏某欄位
  const isHidden = (field: string) => hiddenFields.includes(field)

  useEffect(() => {
    const tenantStr = localStorage.getItem('tenant')
    if (tenantStr) {
      try {
        const t = JSON.parse(tenantStr) as Tenant
        if (t.vehicle_categories?.length) {
          setVehicleOptions(t.vehicle_categories.map((c: any) => typeof c === 'string' ? c : c.name || '').filter(Boolean))
        }
        if (t.payment_methods?.length) {
          setPaymentOptions(t.payment_methods.map((m: any) => typeof m === 'string' ? m : m.name || '').filter(Boolean))
        }
        if (t.form_config?.order?.hidden_fields?.length) {
          setHiddenFields(t.form_config.order.hidden_fields)
        }
      } catch {}
    }
  }, [])

  useEffect(() => {
    if (order) {
      setFormData(buildFormFromOrder(order))
    } else {
      setFormData(getInitialFormData())
    }
    setReturnTripError(null)
    setFlightInfo(null)
    setFlightMsg('')
    setCustomSignFile(null)
    onCustomSignFileChange?.(null)
    // 編輯既有訂單時，恢復客製簽單狀態
    if (order?.custom_sign_image_path) {
      setCustomSignEnabled(true)
      const filename = order.custom_sign_image_path.split(/[/\\]/).pop()
      setCustomSignPreview(`/uploads/custom_signs/${filename}`)
    } else {
      setCustomSignEnabled(false)
      setCustomSignPreview(null)
    }
  }, [order, isOpen])

  useEffect(() => {
    if (autoSelectCustomerId && customers.length > 0) {
      const customer = customers.find(c => c.id === autoSelectCustomerId)
      if (customer) {
        setFormData(prev => ({ ...prev, customer_id: autoSelectCustomerId }))
      }
    }
  }, [autoSelectCustomerId, customers])

  useEffect(() => {
    if (!isOpen || !formData.pickup_date) {
      setOnLeaveDriverIds([])
      return
    }
    const fullDate = `${new Date().getFullYear()}-${formData.pickup_date}`
    let cancelled = false
    leaveApi.getOnDateDriverIds(fullDate)
      .then(ids => { if (!cancelled) setOnLeaveDriverIds(ids) })
      .catch(() => { if (!cancelled) setOnLeaveDriverIds([]) })
    return () => { cancelled = true }
  }, [formData.pickup_date, isOpen])

  const handleFlightLookup = async () => {
    const flight = (formData.flight_number || '').trim()
    const date = formData.pickup_date
    if (!flight || !date) return
    // 將 MM-DD 轉換為 YYYY-MM-DD
    const year = new Date().getFullYear()
    const fullDate = `${year}-${date}`
    setFlightLoading(true)
    setFlightInfo(null)
    setFlightMsg('')
    try {
      const result = await lookupFlight(flight, fullDate)
      if (result.found) {
        setFlightInfo(result)
        setFlightMsg('')
        setFormData(prev => ({
          ...prev,
          departure_time: result.departure_time || '',
          arrival_time: result.arrival_time || '',
          arrival_terminal: result.arrival_terminal || '',
        }))
      } else {
        setFlightMsg(result.message)
      }
    } catch {
      setFlightMsg('查詢失敗，請稍後再試')
    } finally {
      setFlightLoading(false)
    }
  }

  // 勾選來回時自動填入回程預設值
  const handleRoundTripToggle = (checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        is_round_trip: true,
        return_trip: {
          order_category: getReversedCategory(prev.order_category),
          pickup_date: prev.pickup_date,
          pickup_time: prev.pickup_time,
          pickup_location: prev.dropoff_location,
          dropoff_location: prev.pickup_location,
          flight_number: '',
          name_board: false,
          pricing: getEmptyPricing(),
        }
      }))
    } else {
      setFormData(prev => ({ ...prev, is_round_trip: false, return_trip: undefined }))
    }
  }

  const setReturnTrip = (patch: Partial<ReturnTripData>) => {
    setFormData(prev => ({
      ...prev,
      return_trip: { ...prev.return_trip!, ...patch }
    }))
  }

  const setReturnPricing = (patch: Partial<PricingDetail>) => {
    setFormData(prev => ({
      ...prev,
      return_trip: {
        ...prev.return_trip!,
        pricing: { ...prev.return_trip!.pricing, ...patch }
      }
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    if (!formData.passenger_name?.trim()) errors.passenger_name = '請填寫貴賓姓名'
    if (!formData.pickup_location?.trim()) errors.pickup_location = '請填寫上車地點'
    if (!formData.dropoff_location?.trim()) errors.dropoff_location = '請填寫下車地點'
    if (!formData.pickup_date) errors.pickup_date = '請選擇日期'
    if (!formData.pickup_time) errors.pickup_time = '請選擇時間'
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    setFormErrors({})
    setIsSubmitting(true)
    setReturnTripError(null)
    try {
      const submitData: OrderCreateData | OrderUpdateData = {
        order_category: formData.order_category,
        customer_id: formData.customer_id || undefined,
        passenger_name: formData.passenger_name,
        passenger_phone: formData.passenger_phone,
        passenger_count: formData.passenger_count,
        pickup_datetime: formatToISO8601(formData.pickup_date, formData.pickup_time),
        pickup_location: formData.pickup_location,
        dropoff_location: formData.dropoff_location,
        is_round_trip: formData.is_round_trip,
        flight_number: formData.flight_number,
        departure_time: formData.departure_time,
        arrival_time: formData.arrival_time,
        arrival_terminal: formData.arrival_terminal,
        vehicle_type: formData.vehicle_type,
        luggage_count: formData.luggage_count,
        child_seat: formData.child_seat,
        booster_seat: formData.booster_seat,
        name_board: formData.name_board,
        is_signed: formData.is_signed,
        pricing: {
          ...formData.pricing!,
          total_amount: calcTotal(formData.pricing, formData.name_board),
        },
        payment_method: formData.payment_method,
        driver_notes: formData.driver_notes,
        additional_stops: newStopInput.trim()
          ? [...(formData.additional_stops || []), { address: newStopInput.trim(), order: (formData.additional_stops?.length || 0) + 1 }]
          : formData.additional_stops,
        status: formData.status,
        driver_id: formData.driver_id || undefined,
        driver_name: formData.driver_name || undefined,
        driver_phone: formData.driver_phone || undefined,
        vehicle_number: formData.vehicle_number || undefined,
      }
      await onSubmit(submitData)

      // 建立回程訂單
      if (formData.is_round_trip && formData.return_trip) {
        const rt = formData.return_trip
        try {
          await createOrder({
            order_category: rt.order_category,
            customer_id: formData.customer_id || undefined,
            passenger_name: formData.passenger_name,
            passenger_phone: formData.passenger_phone,
            passenger_count: formData.passenger_count,
            pickup_datetime: formatToISO8601(rt.pickup_date, rt.pickup_time),
            pickup_location: rt.pickup_location,
            dropoff_location: rt.dropoff_location,
            is_round_trip: false,
            flight_number: rt.flight_number,
            vehicle_type: formData.vehicle_type,
            luggage_count: formData.luggage_count,
            child_seat: formData.child_seat,
            booster_seat: formData.booster_seat,
            name_board: rt.name_board,
            is_signed: false,
            pricing: {
              ...rt.pricing,
              total_amount: calcTotal(rt.pricing, rt.name_board),
            },
            payment_method: formData.payment_method,
            driver_notes: formData.driver_notes,
            status: '待派車',
          } as any)
        } catch {
          setReturnTripError('主單已建立，但回程訂單建立失敗，請手動新增。')
          return
        }
      }

      onClose()
    } catch (error) {
      console.error('Failed to submit order:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDriverChange = (driverId: string) => {
    const driver = drivers.find(d => d.id === driverId)
    setFormData({
      ...formData,
      driver_id: driverId || '',
      driver_name: driver?.name || '',
      driver_phone: driver?.phone || '',
      vehicle_number: driver?.vehicle_plate || '',
      status: driverId && formData.status === '待派車' ? '已派車' : formData.status
    })
  }

  const handleAddStop = () => {
    if (!newStopInput.trim()) return
    const currentStops = formData.additional_stops || []
    setFormData({ ...formData, additional_stops: [...currentStops, { address: newStopInput.trim(), order: currentStops.length + 1 }] })
    setNewStopInput('')
  }

  const handleRemoveStop = (index: number) => {
    setFormData({ ...formData, additional_stops: (formData.additional_stops || []).filter((_, i) => i !== index) })
  }

  const handleEditStop = (index: number, newValue: string) => {
    const newStops = [...(formData.additional_stops || [])]
    newStops[index] = { ...newStops[index], address: newValue }
    setFormData({ ...formData, additional_stops: newStops })
  }

  const renderAirportSelect = (value: string, onChange: (val: string) => void, placeholder: string) => {
    const isTaoyuan = value && value.startsWith("桃園機場")
    const airportBase = isTaoyuan ? "桃園機場" : (value || "")
    // normalize old format "桃園機場一航廈"→"第1航廈", "桃園機場二航廈"→"第2航廈"
    const normalizeTerminal = (v: string) => {
      if (!v) return "第1航廈"
      if (v === "桃園機場一航廈") return "第1航廈"
      if (v === "桃園機場二航廈") return "第2航廈"
      if (v === "桃園機場三航廈") return "第3航廈"
      return v.replace("桃園機場", "") || "第1航廈"
    }
    const terminal = isTaoyuan ? normalizeTerminal(value) : "第1航廈"
    const selectCls = "flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-2 py-1 text-sm shadow-sm outline-none cursor-pointer"
    return (
      <div className="flex gap-1">
        <select
          value={airportBase}
          onChange={e => {
            const airport = e.target.value
            onChange(airport === "桃園機場" ? `桃園機場${terminal}` : airport)
          }}
          className={`${selectCls} flex-1`}
          required
        >
          <option value="" disabled>{placeholder}</option>
          {AIRPORT_BASES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {isTaoyuan && (
          <select
            value={terminal}
            onChange={e => onChange(`桃園機場${e.target.value}`)}
            className={`${selectCls} w-24`}
          >
            {TAOYUAN_TERMINALS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>
    )
  }

  // 可重用金額區塊
  const renderPricingBlock = (
    pricing: PricingDetail,
    nameboard: boolean,
    onPricingChange: (patch: Partial<PricingDetail>) => void,
    baseRequired = false
  ) => {
    const total = calcTotal(pricing, nameboard)
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-4 gap-2">
          <div className="space-y-1">
            <div className="h-10 flex items-center justify-center rounded-md border border-primary/20 bg-primary/10 dark:bg-primary/20 font-black text-base text-primary tabular-nums">
              {total || 0}
            </div>
            <div className="text-[10px] text-center text-primary font-bold">總金額</div>
          </div>
          <div className="space-y-1">
            <div className="relative">
              <Input type="number" className="h-10 text-base border-primary/30 font-black text-center p-0 pr-14"
                value={pricing.base_fare}
                onChange={e => onPricingChange({ base_fare: parseInt(e.target.value) || 0 })}
                required={baseRequired} />
              <button
                type="button"
                onClick={() => onPricingChange({ is_tax_inclusive: !pricing.is_tax_inclusive })}
                className={`absolute right-1 top-1/2 -translate-y-1/2 h-7 px-1.5 rounded text-[10px] font-bold transition-colors cursor-pointer ${pricing.is_tax_inclusive ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}
                title={pricing.is_tax_inclusive ? '此車資已含稅' : '此車資未含稅（點擊切換）'}
              >
                {pricing.is_tax_inclusive ? '含稅' : '未稅'}
              </button>
            </div>
            <div className="text-[10px] text-center text-primary font-bold">車資</div>
          </div>
          <div className="space-y-1">
            <Input type="number" className="h-10 text-base text-center p-0"
              value={pricing.night_surcharge}
              onChange={e => onPricingChange({ night_surcharge: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">夜間</div>
          </div>
          <div className="space-y-1">
            <Input type="number" className="h-10 text-base text-center p-0"
              value={pricing.cross_district_fee}
              onChange={e => onPricingChange({ cross_district_fee: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">跨區</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 pt-1 border-t border-gray-100 dark:border-gray-700">
          {!isHidden('tax_amount') && (
            <div className="space-y-1">
              <div className="h-10 flex items-center justify-center rounded-md border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 font-black text-base text-orange-600 dark:text-orange-400 tabular-nums">
                {pricing.is_tax_inclusive ? total : Math.round(total * 1.05)}
              </div>
              <div className="text-[10px] text-center text-orange-500 font-bold">含稅總額</div>
            </div>
          )}
          <div className="space-y-1">
            <Input type="number" className="h-10 text-base text-center p-0"
              value={pricing.driver_fee}
              onChange={e => onPricingChange({ driver_fee: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">司機費用</div>
          </div>
          <div className="space-y-1">
            <Input type="number" className="h-10 text-base text-center p-0"
              value={pricing.vendor_rebate}
              onChange={e => onPricingChange({ vendor_rebate: parseInt(e.target.value) || 0 })} />
            <div className="text-[10px] text-center text-gray-500 font-bold">廠商回金</div>
          </div>
          {!isHidden('company_rebate') && (
            <div className="space-y-1">
              <Input type="number" className="h-10 text-base text-center p-0"
                value={pricing.company_rebate}
                onChange={e => onPricingChange({ company_rebate: parseInt(e.target.value) || 0 })} />
              <div className="text-[10px] text-center text-gray-500 font-bold">公司回金</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity" />

      <div className={cn(
        "fixed right-0 top-0 h-full bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden flex flex-col",
        previewImageUrl ? "w-full lg:w-[1000px]" : "w-full md:w-2/3 lg:w-[550px]"
      )}>
        {/* Header */}
        <div className="p-3 border-b flex items-center justify-between bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-sm shrink-0">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white px-1">
            {!order ? '新增訂單' : order.id && order.id !== '' ? '編輯訂單' : '確認訂單'}
          </h2>
          <div className="flex items-center gap-1.5">
            <Button type="button" variant="ghost" onClick={onClose} className="h-8 px-3 text-xs text-gray-500">取消</Button>
            <Button type="submit" form="order-form" disabled={isSubmitting}
              className="h-8 px-4 text-xs font-bold bg-red-600 hover:bg-red-700 text-white">
              {isSubmitting ? '儲存中...' : !order ? '建立' : order.id && order.id !== '' ? '更新' : '確認建立'}
            </Button>
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-gray-400">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body：桌面有圖片時左右雙欄，手機有圖片時上下 */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
          {/* 圖片預覽：手機收合式、桌機左欄固定 */}
          {previewImageUrl && (
            <ImagePreviewPanel url={previewImageUrl} />
          )}

          {/* 表單 */}
          <div className="flex-1 overflow-y-auto overflow-x-visible">
        <form id="order-form" onSubmit={handleSubmit} className="p-4 space-y-4 scrollbar-thin"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const t = e.target as HTMLElement
              if (t.tagName === 'TEXTAREA') return
              if (t.tagName === 'BUTTON' && (t as HTMLButtonElement).type === 'submit') return
              e.preventDefault()
            }
          }}>

          {/* AI 原始文字對照 */}
          {aiRawText && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 p-3">
              <div className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1">AI 解析原文</div>
              <p className="text-xs text-blue-800 dark:text-blue-200 whitespace-pre-wrap leading-relaxed">{aiRawText}</p>
            </div>
          )}

          {/* 類別 + 狀態 */}
          <div className="flex gap-2">
            <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-md border border-gray-200 dark:border-gray-700 flex-1">
              {['送機', '接機', '其他'].map(cat => (
                <Button key={cat} type="button" variant={formData.order_category === cat ? "default" : "ghost"} size="sm"
                  onClick={() => {
                    if (cat === formData.order_category) return
                    const needsAirportPickup = cat === '接機'
                    const needsAirportDropoff = cat === '送機'
                    const isAirport = (v: string) => AIRPORT_BASES.some(a => v.startsWith(a))
                    setFormData({
                      ...formData,
                      order_category: cat,
                      pickup_location: needsAirportPickup
                        ? (isAirport(formData.pickup_location) ? formData.pickup_location : '桃園機場第1航廈')
                        : formData.pickup_location,
                      dropoff_location: needsAirportDropoff
                        ? (isAirport(formData.dropoff_location) ? formData.dropoff_location : '桃園機場第1航廈')
                        : formData.dropoff_location,
                    })
                  }}
                  className={cn("flex-1 h-8 px-0 text-xs font-bold transition-all", formData.order_category === cat ? "shadow-sm" : "text-gray-500")}>
                  {cat}
                </Button>
              ))}
            </div>
            <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
              className="w-[120px] h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-xs font-bold text-pink-600 outline-none cursor-pointer">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* 日期 / 時間 / 航班 */}
          <div className={`grid gap-2 overflow-visible ${isHidden('flight_number') ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <MonthDayPicker value={formData.pickup_date}
              onChange={value => { setFormData({ ...formData, pickup_date: value }); setFlightInfo(null); setFlightMsg(''); setTimeout(() => timePickerRef.current?.focus(), 100) }}
              placeholder="MM-DD" />
            <TimePicker ref={timePickerRef} value={formData.pickup_time}
              onChange={value => setFormData({ ...formData, pickup_time: value })} placeholder="選擇時間" />
            {!isHidden('flight_number') && (
              <div className="flex gap-1">
                <InputWithClear placeholder="航班編號" className="h-10 text-base"
                  value={formData.flight_number || ''}
                  onChange={e => { setFormData({ ...formData, flight_number: e.target.value }); setFlightInfo(null); setFlightMsg('') }}
                  onClear={() => { setFormData({ ...formData, flight_number: undefined }); setFlightInfo(null); setFlightMsg('') }} />
                <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0"
                  disabled={!formData.flight_number || !formData.pickup_date || flightLoading}
                  onClick={handleFlightLookup}>
                  <Search className={`w-4 h-4 ${flightLoading ? 'animate-pulse' : ''}`} />
                </Button>
              </div>
            )}
          </div>

          {/* 航班資訊顯示 */}
          {!isHidden('flight_number') && flightInfo && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300">
              <span className="font-semibold">{flightInfo.airline} {flightInfo.flight_number}</span>
              {' | '}
              <span>{flightInfo.departure_airport} → {flightInfo.arrival_airport}</span>
              {'  '}
              <span>起飛 {flightInfo.departure_time}</span>
              {' · '}
              <span>抵達 {flightInfo.arrival_time}</span>
              {flightInfo.arrival_terminal && (
                <span className="ml-1 font-medium">航廈 {flightInfo.arrival_terminal}</span>
              )}
            </div>
          )}
          {!isHidden('flight_number') && flightMsg && (
            <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500">
              {flightMsg}
            </div>
          )}

          {/* 上下車地點 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              {formData.order_category === '接機'
                ? renderAirportSelect(formData.pickup_location, val => { setFormData({ ...formData, pickup_location: val }); setFormErrors(prev => ({ ...prev, pickup_location: '' })) }, "上車機場 *")
                : <AddressAutocomplete placeholder="上車地點 *" value={formData.pickup_location}
                    onChange={val => { setFormData({ ...formData, pickup_location: val }); setFormErrors(prev => ({ ...prev, pickup_location: '' })) }} required />}
              {formErrors.pickup_location && <p className="text-xs text-red-500 mt-0.5">{formErrors.pickup_location}</p>}
            </div>
            <div>
              {formData.order_category === '送機'
                ? renderAirportSelect(formData.dropoff_location, val => { setFormData({ ...formData, dropoff_location: val }); setFormErrors(prev => ({ ...prev, dropoff_location: '' })) }, "下車機場 *")
                : <AddressAutocomplete placeholder="下車地點 *" value={formData.dropoff_location}
                    onChange={val => { setFormData({ ...formData, dropoff_location: val }); setFormErrors(prev => ({ ...prev, dropoff_location: '' })) }} required />}
              {formErrors.dropoff_location && <p className="text-xs text-red-500 mt-0.5">{formErrors.dropoff_location}</p>}
            </div>
          </div>

          {/* 車型 + 人數/行李/座椅 */}
          <div className="flex gap-2">
            <select value={formData.vehicle_type} onChange={e => setFormData({ ...formData, vehicle_type: e.target.value })}
              className="flex h-10 flex-[1.5] rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer" required>
              <option value="">選擇車型 *</option>
              {vehicleOptions.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <Counter value={formData.passenger_count} min={1} max={8} onChange={value => setFormData({ ...formData, passenger_count: value })} label="人數" />
            {!isHidden('luggage_count') && <Counter value={formData.luggage_count} min={0} max={8} onChange={value => setFormData({ ...formData, luggage_count: value })} label="行李" />}
            {!isHidden('child_seat') && <Counter value={formData.child_seat} min={0} max={4} onChange={value => setFormData({ ...formData, child_seat: value })} label="兒童座椅" />}
            {!isHidden('booster_seat') && <Counter value={formData.booster_seat} min={0} max={4} onChange={value => setFormData({ ...formData, booster_seat: value })} label="增高墊" />}
          </div>

          {/* 客戶 */}
          {!isHidden('customer') && (
            <div className="flex gap-2 items-center">
              <CustomerCombobox customers={customers} value={formData.customer_id || ''}
                onChange={(customerId) => setFormData(prev => ({ ...prev, customer_id: customerId || undefined }))}
                className="flex-1" />
              <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 text-primary border-primary/40 hover:bg-primary/5" title="快速新增客戶" onClick={onRequestAddCustomer}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* 乘客姓名 / 手機 */}
          <div className={`grid gap-2 ${isHidden('passenger_phone') ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div>
              <InputWithClear placeholder="貴賓姓名 *" className={`h-10 text-base ${formErrors.passenger_name ? 'border-red-400' : ''}`} value={formData.passenger_name}
                onChange={e => { setFormData(prev => ({ ...prev, passenger_name: e.target.value })); setFormErrors(prev => ({ ...prev, passenger_name: '' })) }}
                onClear={() => setFormData(prev => ({ ...prev, passenger_name: '' }))} required />
              {formErrors.passenger_name && <p className="text-xs text-red-500 mt-0.5">{formErrors.passenger_name}</p>}
            </div>
            {!isHidden('passenger_phone') && (
              <InputWithClear placeholder="手機號碼" className="h-10 text-base" value={formData.passenger_phone}
                onChange={e => setFormData({ ...formData, passenger_phone: e.target.value })}
                onClear={() => setFormData({ ...formData, passenger_phone: '' })} />
            )}
          </div>

          {/* 主單金額 */}
          {!isHidden('pricing') && (
            <div className="bg-primary/5 dark:bg-primary/10 p-3 rounded-md border border-primary/10">
              {renderPricingBlock(formData.pricing, formData.name_board,
                patch => setFormData({ ...formData, pricing: { ...formData.pricing, ...patch } }), true)}
            </div>
          )}

          {/* 來回 | 舉牌 | 簽單 | 客製簽單 */}
          <div className="grid grid-cols-4 gap-1 p-1 bg-gray-50 dark:bg-gray-900/50 rounded-md border border-gray-100 dark:border-gray-800">
            {!isHidden('is_round_trip') ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.is_round_trip}
                  onChange={e => handleRoundTripToggle(e.target.checked)}
                  className="w-5 h-5 text-primary border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-gray-700 dark:text-gray-300">來回</span>
              </label>
            ) : <div />}
            {!isHidden('name_board') ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.name_board}
                  onChange={e => {
                    const checked = e.target.checked
                    const currentNotes = formData.driver_notes || ''
                    let newNotes = currentNotes
                    if (checked && !currentNotes.includes('舉牌')) {
                      newNotes = currentNotes ? `${currentNotes} 舉牌` : '舉牌'
                    } else if (!checked) {
                      newNotes = currentNotes.replace(/\s*舉牌\s*/g, ' ').trim()
                    }
                    setFormData({ ...formData, name_board: checked, driver_notes: newNotes })
                  }}
                  className="w-5 h-5 text-primary border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-gray-700 dark:text-gray-300">舉牌</span>
              </label>
            ) : <div />}
            {!isHidden('is_signed') ? (
              <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
                <input type="checkbox" checked={formData.is_signed}
                  onChange={e => {
                    const checked = e.target.checked
                    setFormData({ ...formData, is_signed: checked })
                    // 互斥：勾選簽單時取消客製簽單
                    if (checked) {
                      setCustomSignEnabled(false)
                      setCustomSignFile(null)
                      setCustomSignPreview(null)
                      onCustomSignFileChange?.(null)
                    }
                  }}
                  className="w-5 h-5 text-primary border-gray-300 rounded" />
                <span className="ml-2 text-sm font-bold text-gray-700 dark:text-gray-300">簽單</span>
              </label>
            ) : <div />}
            <label className="flex items-center justify-center cursor-pointer p-2 hover:bg-white dark:hover:bg-gray-800 rounded transition-all h-10">
              <input type="checkbox" checked={customSignEnabled}
                onChange={e => {
                  const checked = e.target.checked
                  setCustomSignEnabled(checked)
                  // 互斥：勾選客製簽單時取消簽單
                  if (checked) {
                    setFormData({ ...formData, is_signed: false })
                  } else {
                    setCustomSignFile(null)
                    setCustomSignPreview(null)
                    onCustomSignFileChange?.(null)
                  }
                }}
                className="w-5 h-5 text-orange-500 border-gray-300 rounded" />
              <span className="ml-2 text-sm font-bold text-orange-600 dark:text-orange-400">客製簽單</span>
            </label>
          </div>

          {/* 客製簽單上傳區 */}
          {customSignEnabled && (
            <div className="rounded-lg border-2 border-dashed border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10 p-3 space-y-2">
              <p className="text-xs font-medium text-orange-700 dark:text-orange-400">上傳客製簽單底圖（司機簽名時會顯示在上方）</p>
              <input
                type="file"
                accept="image/*"
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200 cursor-pointer"
                onChange={e => {
                  const f = e.target.files?.[0] || null
                  setCustomSignFile(f)
                  onCustomSignFileChange?.(f)
                  if (f) {
                    const url = URL.createObjectURL(f)
                    setCustomSignPreview(url)
                  } else {
                    setCustomSignPreview(null)
                  }
                }}
              />
              {customSignPreview && (
                <img src={customSignPreview} alt="預覽" className="max-h-32 rounded border border-orange-200 object-contain" />
              )}
            </div>
          )}

          {/* 回程區塊 */}
          {formData.is_round_trip && formData.return_trip && (
            <div className="border-2 border-indigo-200 dark:border-indigo-700 rounded-xl p-4 space-y-3 bg-indigo-50/50 dark:bg-indigo-900/10">
              <div className="flex items-center justify-between">
                <span className="text-sm font-black text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
                  <RotateCcw className="h-4 w-4" />
                  回程訂單
                </span>
                <span className="text-xs text-indigo-400">提交後自動建立（不含司機）</span>
              </div>

              {/* 回程類別 */}
              <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-md border border-gray-200 dark:border-gray-700">
                {['送機', '接機', '其他'].map(cat => (
                  <Button key={cat} type="button" variant={formData.return_trip!.order_category === cat ? "default" : "ghost"} size="sm"
                    onClick={() => setReturnTrip({ order_category: cat })}
                    className={cn("flex-1 h-8 px-0 text-xs font-bold transition-all", formData.return_trip!.order_category === cat ? "shadow-sm" : "text-gray-500")}>
                    {cat}
                  </Button>
                ))}
              </div>

              {/* 回程日期 / 時間 / 航班 */}
              <div className="grid grid-cols-3 gap-2">
                <MonthDayPicker value={formData.return_trip.pickup_date}
                  onChange={value => setReturnTrip({ pickup_date: value })} placeholder="MM-DD" />
                <TimePicker value={formData.return_trip.pickup_time}
                  onChange={value => setReturnTrip({ pickup_time: value })} placeholder="選擇時間" />
                <InputWithClear placeholder="航班編號" className="h-10 text-base"
                  value={formData.return_trip.flight_number || ''}
                  onChange={e => setReturnTrip({ flight_number: e.target.value })}
                  onClear={() => setReturnTrip({ flight_number: '' })} />
              </div>

              {/* 回程上下車地點 */}
              <div className="grid grid-cols-2 gap-2">
                <AddressAutocomplete placeholder="上車地點 *" value={formData.return_trip.pickup_location}
                  onChange={val => setReturnTrip({ pickup_location: val })} required />
                <AddressAutocomplete placeholder="下車地點 *" value={formData.return_trip.dropoff_location}
                  onChange={val => setReturnTrip({ dropoff_location: val })} required />
              </div>

              {/* 回程舉牌 */}
              <label className="flex items-center gap-2 cursor-pointer w-fit">
                <input type="checkbox" checked={formData.return_trip.name_board}
                  onChange={e => setReturnTrip({ name_board: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded" />
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300">舉牌</span>
              </label>

              {/* 回程金額 */}
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-md border border-indigo-100 dark:border-indigo-800">
                {renderPricingBlock(formData.return_trip.pricing, formData.return_trip.name_board, setReturnPricing)}
              </div>

              {returnTripError && (
                <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">{returnTripError}</div>
              )}
            </div>
          )}

          {/* 付款方式 + 司機 */}
          <div className="grid grid-cols-2 gap-2">
            <select value={formData.payment_method} onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer" required>
              <option value="">選擇付款方式 *</option>
              {paymentOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={formData.driver_id || ''} onChange={e => handleDriverChange(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-2 text-sm shadow-sm outline-none cursor-pointer">
              <option value="">未指派司機</option>
              {drivers.map(driver => {
                const isOnLeave = onLeaveDriverIds.includes(driver.id)
                return (
                  <option key={driver.id} value={driver.id} disabled={isOnLeave}>
                    {driver.name} - {driver.vehicle_plate}{isOnLeave ? ' (請假)' : ''}
                  </option>
                )
              })}
            </select>
          </div>

          {/* 其他上車點 */}
          {!isHidden('additional_stops') && <div className="space-y-2">
            <div className="flex gap-2">
              <div className="flex-1">
                <AddressAutocomplete placeholder="新增第三方上車地點..." value={newStopInput} onChange={setNewStopInput} />
              </div>
              <Button type="button" onClick={handleAddStop} variant="outline" size="icon" className="h-10 w-10 shrink-0">
                <Plus className="h-5 w-5" />
              </Button>
            </div>
            {formData.additional_stops && formData.additional_stops.length > 0 && (
              <div className="space-y-2 bg-gray-50 dark:bg-gray-900/50 p-2 rounded-md border border-gray-100 dark:border-gray-800">
                {formData.additional_stops.map((stop, index) => (
                  <div key={index} className="flex items-center gap-2 group">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-xs font-bold shrink-0">{index + 1}</div>
                    <Input value={stop.address} onChange={e => handleEditStop(index, e.target.value)}
                      className="h-8 text-sm flex-1 border-transparent focus:border-input bg-transparent hover:bg-white dark:hover:bg-gray-800 transition-colors" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveStop(index)}
                      className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>}

          {/* 備註 */}
          {!isHidden('driver_notes') && (
            <div className="space-y-2">
              <Textarea placeholder="請輸入給司機的備註資訊..." className="text-base bg-gray-50/10 min-h-[80px] resize-none"
                rows={2} value={formData.driver_notes || ''}
                onChange={e => setFormData({ ...formData, driver_notes: e.target.value })} />
              {formData.driver_notes && (
                <div className="text-xs text-gray-500 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-900/30 rounded border border-gray-200 dark:border-gray-700">
                  <div className="font-medium mb-1">預覽（URL 將變成可點擊的鏈接）：</div>
                  <div className="text-gray-700 dark:text-gray-300 break-words">
                    {(() => {
                      const urlRegex = /(https?:\/\/[^\s]+)/g
                      const parts = formData.driver_notes!.split(urlRegex)
                      return parts.map((part, index) => {
                        if (urlRegex.test(part)) {
                          return (
                            <a
                              key={index}
                              href={part}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {part}
                            </a>
                          )
                        }
                        return part
                      })
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
          </div>{/* end right panel */}
        </div>{/* end flex body */}
      </div>{/* end drawer */}
    </>
  )
}
